/**
 * Exact fixed-scale decimal arithmetic for purchasing wire strings.
 *
 * MONEY/QTY RULE (SPEC-CONVENTIONS): a JS `number` never holds an amount, a
 * quantity, a unit cost, an FX rate or a percentage. Every value on the
 * purchasing wire is a decimal STRING at a fixed scale:
 *
 *   quantity / unit cost   NUMERIC(20,6)  "12.500000"
 *   amount                 NUMERIC(20,4)  "1250.0000"
 *   fx rate                NUMERIC(20,10) "3.7500000000"
 *   percentage             NUMERIC(9,6)   "5.000000"
 *
 * All arithmetic here is BigInt on the scaled integer, so it is exact.
 * Multiplication and rescaling round HALF-EVEN, matching the server's
 * decimal rounding — but every client-computed value is a PREVIEW only.
 * The server's response is always authoritative.
 */

/** Wire scales declared by the purchasing schemas. */
export const QTY_SCALE = 6;
export const COST_SCALE = 6;
export const AMOUNT_SCALE = 4;
export const FX_SCALE = 10;
export const PCT_SCALE = 6;

const DECIMAL_RE = /^(-?)(\d+)(?:\.(\d*))?$/;

/** Thrown when a string is not a well-formed plain decimal. */
export class DecimalFormatError extends RangeError {
  constructor(value: string) {
    super(`Not a decimal string: ${JSON.stringify(value)}`);
    this.name = "DecimalFormatError";
  }
}

/** Scaled-integer form of a decimal string: value === units / 10^scale. */
export function toUnits(value: string, scale: number): bigint {
  const match = DECIMAL_RE.exec(value.trim());
  if (match === null) throw new DecimalFormatError(value);
  const negative = match[1] === "-";
  const integerDigits = match[2] ?? "0";
  const fractionDigits = match[3] ?? "";
  if (fractionDigits.length <= scale) {
    const padded = fractionDigits.padEnd(scale, "0");
    const magnitude = BigInt(integerDigits + padded);
    return negative ? -magnitude : magnitude;
  }
  // Excess precision is rounded half-even onto the target scale.
  const kept = fractionDigits.slice(0, scale);
  const rest = fractionDigits.slice(scale);
  let magnitude = BigInt(integerDigits + kept);
  const half = "5".padEnd(rest.length, "0");
  if (rest > half || (rest === half && magnitude % 2n === 1n)) magnitude += 1n;
  return negative ? -magnitude : magnitude;
}

/** Render scaled units back as a canonical fixed-scale decimal string. */
export function fromUnits(units: bigint, scale: number): string {
  const negative = units < 0n;
  const digits = (negative ? -units : units).toString().padStart(scale + 1, "0");
  const integerPart = scale === 0 ? digits : digits.slice(0, -scale);
  const fractionPart = scale === 0 ? "" : digits.slice(-scale);
  const body = scale === 0 ? integerPart : `${integerPart}.${fractionPart}`;
  // Negative zero is never canonical.
  return negative && /[1-9]/.test(digits) ? `-${body}` : body;
}

/** Canonicalize any well-formed decimal string onto an exact wire scale. */
export function toScale(value: string, scale: number): string {
  return fromUnits(toUnits(value, scale), scale);
}

/**
 * Canonicalize free user input onto a wire scale, or null when the text is
 * not a decimal. Accepts Arabic-Indic digits and an Arabic decimal separator
 * so an RTL operator can type naturally; never uses `Number()`.
 */
export function parseDecimalInput(raw: string, scale: number): string | null {
  const normalized = raw
    .trim()
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    // Arabic decimal separator → ".", then drop grouping/bidi marks.
    .replace(/٫/g, ".")
    .replace(/[٬,⁦-⁩‎‏\s]/g, "");
  if (normalized === "" || !DECIMAL_RE.test(normalized)) return null;
  try {
    return toScale(normalized, scale);
  } catch {
    return null;
  }
}

/** Sum decimal strings exactly at `scale`. An empty list is zero. */
export function addDecimals(values: readonly string[], scale: number): string {
  let total = 0n;
  for (const value of values) total += toUnits(value, scale);
  return fromUnits(total, scale);
}

/** a − b at `scale`. */
export function subtractDecimal(a: string, b: string, scale: number): string {
  return fromUnits(toUnits(a, scale) - toUnits(b, scale), scale);
}

/**
 * a × b rendered at `resultScale`, rounded half-even. Give each operand its
 * own scale (e.g. qty 6 × unit cost 6 → amount 4).
 */
export function multiplyDecimal(
  a: string,
  aScale: number,
  b: string,
  bScale: number,
  resultScale: number,
): string {
  const product = toUnits(a, aScale) * toUnits(b, bScale);
  return rescaleUnits(product, aScale + bScale, resultScale);
}

/** Rescale a scaled BigInt, rounding half-even when digits are dropped. */
function rescaleUnits(units: bigint, fromScale: number, toScaleValue: number): string {
  if (toScaleValue >= fromScale) {
    return fromUnits(units * 10n ** BigInt(toScaleValue - fromScale), toScaleValue);
  }
  const divisor = 10n ** BigInt(fromScale - toScaleValue);
  const negative = units < 0n;
  const magnitude = negative ? -units : units;
  let quotient = magnitude / divisor;
  const remainder = magnitude % divisor;
  const twice = remainder * 2n;
  if (twice > divisor || (twice === divisor && quotient % 2n === 1n)) quotient += 1n;
  return fromUnits(negative ? -quotient : quotient, toScaleValue);
}

/** −1 | 0 | 1 comparison of two decimal strings evaluated at `scale`. */
export function compareDecimal(a: string, b: string, scale: number): -1 | 0 | 1 {
  const left = toUnits(a, scale);
  const right = toUnits(b, scale);
  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
}

export function isZeroDecimal(value: string, scale: number): boolean {
  return toUnits(value, scale) === 0n;
}

export function isNegativeDecimal(value: string, scale: number): boolean {
  return toUnits(value, scale) < 0n;
}

/** Larger of two decimal strings at `scale`. */
export function maxDecimal(a: string, b: string, scale: number): string {
  return compareDecimal(a, b, scale) >= 0 ? toScale(a, scale) : toScale(b, scale);
}

/** `value × pct / 100` at `resultScale` (pct is a NUMERIC(9,6) string). */
export function applyPercent(
  value: string,
  valueScale: number,
  pct: string,
  resultScale: number,
): string {
  const product = toUnits(value, valueScale) * toUnits(pct, PCT_SCALE);
  return rescaleUnits(product, valueScale + PCT_SCALE + 2, resultScale);
}

/**
 * Whole-percent progress (0..100) of `part` against `whole`, for progress
 * bars only. Returns 0 when `whole` is zero. The BigInt ratio is computed
 * exactly first, so no decimal string is ever passed through `Number()`.
 */
export function progressPercent(part: string, whole: string, scale: number): number {
  const wholeUnits = toUnits(whole, scale);
  if (wholeUnits <= 0n) return 0;
  const partUnits = toUnits(part, scale);
  if (partUnits <= 0n) return 0;
  const pct = (partUnits * 100n) / wholeUnits;
  return pct > 100n ? 100 : Number(pct);
}

/** Remaining orderable/receivable quantity, floored at zero. */
export function remainingQty(ordered: string, received: string): string {
  const remaining = toUnits(ordered, QTY_SCALE) - toUnits(received, QTY_SCALE);
  return fromUnits(remaining > 0n ? remaining : 0n, QTY_SCALE);
}

/**
 * Maximum quantity a receipt may still accept for a PO line, i.e.
 * `ordered × (1 + tolerance%) − already received`, floored at zero.
 */
export function receivableQty(ordered: string, received: string, tolerancePct: string): string {
  const allowance = applyPercent(ordered, QTY_SCALE, tolerancePct, QTY_SCALE);
  const allowed = toUnits(ordered, QTY_SCALE) + toUnits(allowance, QTY_SCALE);
  const remaining = allowed - toUnits(received, QTY_SCALE);
  return fromUnits(remaining > 0n ? remaining : 0n, QTY_SCALE);
}
